import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { useForm, isFormWithHtmlDirty } from '../../helpers'

import { useUser } from '../../contexts'

import {
  EditorWithPreview,
  HtmlEditor,
  LeavingOnDirtyForm,
  MediaSelector,
  PreviewPage,
  SpecialtiesSelector
} from '../specialized-components'
import { icons, Button, Card, ExternalLinkButton, FormField, Icon, Input, Select, SubmitButton } from '../ui-blocks'

import { CourseBuilder } from './CourseBuilder'

import { courseFields, levelOptions, statusOptions, teamSizeOptions } from './course-fields'


// What the course page will show, from the form as it is now (SPEC.md, "Editors").
const coursePreviewProps = (values, authorName) => {
  // As the course builder counts them: lessons are the sections that hold topics.
  const lessonCount = (values.sections || []).filter(section => section.children || /lesson/.test(section.type || '')).length
  const price = values.price === '' || values.price === undefined || values.price === null
    ? ''
    : (Number(values.price) === 0 ? 'Free' : `$${Number(values.price).toLocaleString()}`)

  return {
    kicker: 'Course',
    title: values.title || '',
    byline: authorName ? `By ${authorName}` : '',
    facts: [
      { label: 'Lessons', value: lessonCount || '' },
      { label: 'Price', value: price },
      { label: 'Team size', value: values.teamSize || '' },
      { label: 'Skill level', value: values.level || '' }
    ],
    media: values.featuredMedia,
    html: values.content || ''
  }
}


export const EditCourse = ({ course, lessons, topics, availableSpecialties, onUpdate, onDelete }) => {
  const [form, setFormValues, validateForm] = useForm(courseFields)
  const { userAvatar } = useUser()
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setFormValues(course)
  }, [course]) // eslint-disable-line react-hooks/exhaustive-deps


  const handleUpdate = async () => {
    const isFormValid = validateForm()

    if (!isFormValid) {
      return
    }

    setIsLoading(true)

    await onUpdate(form.values)

    setIsLoading(false)
  }


  if (!Object.keys(form.values).length) {
    return null
  }


  const isNew = !course.id
  const isFreeCourse = !!String(form.fields.price.value)
    && form.fields.price.isValid
    && (Number(form.fields.price.value) === 0)

  const isFormDirty = isFormWithHtmlDirty(form)


  return (
    <>
      <LeavingOnDirtyForm isDirty={isFormDirty} />

      <div className="editor-header">
        <h1>{isNew ? 'New course' : (form.values.title || 'Edit course')}</h1>

        <div className="d-sm-flex flex-grow-1 justify-content-end align-items-center">
          <div className="mb-2 mb-sm-0 mr-sm-3">
            <Select
              options={statusOptions}
              {...form.fields.status.getStatusProps()}
              {...form.fields.status.getProps()}
            />
          </div>
          <div className="text-right">
            {!isNew && (
              <ExternalLinkButton
                href={course.link}
                className="mr-3"
                variant="secondary"
                disabled={isFormDirty}
              >
                Open live page
              </ExternalLinkButton>
            )}
            <SubmitButton
              onClick={handleUpdate}
              isLoading={isLoading}
              isValid={form.isValid}
            >
              {isNew ? 'Create' : 'Update'}
            </SubmitButton>
          </div>
        </div>
      </div>

      <EditorWithPreview preview={<PreviewPage {...coursePreviewProps(form.values, userAvatar?.name)} />}>

        <FormField label="Title" inputField={form.fields.title} className="mb-4">
          <Input
            id="title"
            className="form-control-lg"
            placeholder="What learners will see as the course name"
            {...form.fields.title?.getStatusProps()}
            {...form.fields.title?.getProps()}
          />
        </FormField>

        <Card title="Description" className="mb-5">
          <HtmlEditor {...form.fields.content?.getProps()} />
        </Card>

        <div className="mb-5">
          <MediaSelector
            media={form.fields.featuredMedia.value}
            setMedia={form.fields.featuredMedia.setValue}
          />
          {!form.fields.featuredMedia.isValid && (
            <div className="text-danger small mt-2">
              Add a featured image or video.
            </div>
          )}
        </div>

        <Card title="Details" className="mb-5">
          <FormField label="Price"
            labelSuffix={isFreeCourse && (
              <span className="badge rounded-pill bg-success">Free course</span>
            )}
            inputField={form.fields.price}
          >
            <Input
              id="price"
              prefix="USD"
              type="number"
              min="0"
              {...form.fields.price.getStatusProps()}
              {...form.fields.price?.getProps()}
            />
          </FormField>

          <div className="row">
            <div className="col-sm-6">
              <FormField label="Team size" inputField={form.fields.teamSize}>
                <Select
                  id="teamSize"
                  options={teamSizeOptions}
                  {...form.fields.teamSize.getStatusProps()}
                  {...form.fields.teamSize.getProps()}
                />
              </FormField>
            </div>
            <div className="col-sm-6">
              <FormField label="Skill level" inputField={form.fields.level}>
                <Select
                  id="level"
                  options={levelOptions}
                  {...form.fields.level.getStatusProps()}
                  {...form.fields.level.getProps()}
                />
              </FormField>
            </div>
          </div>
        </Card>

        <Card title="Specialties" className="mb-5">
          <SpecialtiesSelector
            specialties={form.fields.specialties.value}
            setSpecialties={form.fields.specialties.setValue}
            availableSpecialties={availableSpecialties}
          />
        </Card>

        {!isNew && (
          <div className="mb-5">
            <CourseBuilder
              sections={form.fields.sections.value}
              setSections={form.fields.sections.setValue}
              availableLessons={lessons}
              availableTopics={topics}
            />
          </div>
        )}

      {!isNew && (
        <span className="text-muted">
          <Button
            onClick={onDelete}
            variant="outline-danger"
            className="btn-danger-on-hover mr-3 btn-sm px-3"
          >
            <Icon icon={icons.delete} className="mr-2" />
            Delete this course
          </Button>
        </span>
      )}

      </EditorWithPreview>

    </>
  )
}


EditCourse.propTypes = {
  course: PropTypes.object,
  lessons: PropTypes.arrayOf(PropTypes.object),
  topics: PropTypes.arrayOf(PropTypes.object),
  availableSpecialties: PropTypes.array.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
}
